/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  HStack,
  Button,
  Tag,
  Badge,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  FormHelperText,
  Input,
  Select,
  Checkbox,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiPlus, FiUpload } from "react-icons/fi";
import { DataTable } from "components/DataTable";
import { getColumnsStokCihaz } from "components/DataTable/columnsStokCihaz";
import columnsStokAksesuar from "components/DataTable/columnsStokAksesuar";
import FilterPanel from "components/FilterPanel/FilterPanel";
import StokContextMenu from "components/ContextMenu/StokContextMenu";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { useBayiOptions } from "hooks/useBayiOptions";
import { useUserRole } from "hooks/useUserRole";
import { useColumnOrder } from "hooks/useColumnOrder";
import { useFiltersRef } from "hooks/useFiltersRef";

const STATUS_OPTIONS = [
  { value: "hazir", label: "Kullanıma Hazır" },
  { value: "kiralandi", label: "Kiralandı" },
  { value: "satildi", label: "Satıldı" },
  { value: "ariza", label: "Arıza" },
];

const STATUS_LABELS = {
  hazir: "Kullanıma Hazır",
  kiralandi: "Kiralandı",
  satildi: "Satıldı",
  ariza: "Arıza",
};

const STATUS_COLOR = {
  hazir: "green",
  kullanimda: "blue",
  kiralandi: "cyan",
  satildi: "orange",
  ariza: "red",
};

// Cihaz model listesi
const DEVICE_MODELS = [
  "Teltonika FMB920",
  "Teltonika FMB140",
  "Teltonika FMB120",
  "Armoli L300",
  "Armoli M300",
  "Armoli P300",
  "Ortem M12",
  "GS900",
];

// Model'e göre protokol belirleme fonksiyonu
const getProtocolFromModel = (model) => {
  if (!model) return null;
  const modelLower = model.toLowerCase();
  if (modelLower.includes("ortem") || modelLower.includes("m12")) {
    return "xtakip";
  }
  if (modelLower.includes("teltonika") || modelLower.includes("fmb")) {
    return "teltonika";
  }
  if (modelLower.includes("armoli")) {
    return "armoli";
  }
  if (modelLower.includes("gt06") || modelLower.includes("gs900")) {
    return "gt06";
  }
  return null;
};

const formatTedarikciName = (value = "") =>
  String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toLocaleUpperCase("tr-TR") + part.slice(1).toLocaleLowerCase("tr-TR"))
    .join(" ");

export default function StokYonetimi() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const addModal = useDisclosure();
  const bulkModal = useDisclosure();
  const addAksesuarModal = useDisclosure();
  const userRole = useUserRole();
  
  // Role kontrolü - Tüm roller erişebilir (admin, bayi, teknik_eleman)
  // Bu sayfa zaten tüm roller için açık

  // Filtre state (kullanıcının yazdığı/seçtiği değerler)
  const [imeiFilter, setImeiFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [alisTarihiFilter, setAlisTarihiFilter] = useState("");
  // Bayi filtresi default olarak "ixpilot" (admin ve teknik_eleman için)
  const [bayiFilter, setBayiFilter] = useState("ixpilot");
  
  // Uygulanan filtreler (Listele butonuna basınca bu değerler kullanılacak)
  const [appliedImeiFilter, setAppliedImeiFilter] = useState("");
  const [appliedModelFilter, setAppliedModelFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedAlisTarihiFilter, setAppliedAlisTarihiFilter] = useState("");
  
  // Arama yapılıp yapılmadığını takip et
  const [hasSearched, setHasSearched] = useState(false);
  
  const [stockItems, setStockItems] = useState([]);
  const [availableAlisTarihleri, setAvailableAlisTarihleri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [contextMenu, setContextMenu] = useState({ position: null, item: null });

  // Aksesuarlar state
  const [aksesuarlar, setAksesuarlar] = useState([]);
  const [aksesuarlarLoading, setAksesuarlarLoading] = useState(false);
  const [aksesuarlarError, setAksesuarlarError] = useState(null);
  const [aksesuarSearch, setAksesuarSearch] = useState("");
  const [stokTuru, setStokTuru] = useState("cihaz");
  const [newAksesuar, setNewAksesuar] = useState({
    name: "",
    quantity: "0",
  });
  const [savingAksesuar, setSavingAksesuar] = useState(false);
  const [editingAksesuar, setEditingAksesuar] = useState(null);

  const [newStock, setNewStock] = useState({
    imei: "",
    model: "",
    alis_tarihi: "",
    tedarikci: "",
    notlar: "",
    bataryali: false,
    bayi_id: "",
  });
  const [savingStock, setSavingStock] = useState(false);

  const [bulkStock, setBulkStock] = useState({
    imeiList: "",
    model: "",
    alis_tarihi: "",
    tedarikci: "",
    notlar: "",
    bataryali: false,
    bayi_id: "",
  });
  const [savingBulkStock, setSavingBulkStock] = useState(false);

  // Not düzenleme modal state
  const [noteModal, setNoteModal] = useState({
    isOpen: false,
    id: null,
    notlar: "",
  });
  const [editStockModal, setEditStockModal] = useState({
    isOpen: false,
    id: null,
    tedarikci: "",
    alis_tarihi: "",
  });

  // Bayiler hook'u sadece admin ve teknik_eleman için aktif (yetkili bayi ve saas bayi göremez)
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const {
    bayiler,
    loading: bayilerLoading,
  } = useBayiOptions(shouldLoadBayiler);

  // Filtreleri ref ile tut - closure sorununu çözmek için
  const filtersRef = useFiltersRef({
    imeiFilter,
    modelFilter,
    statusFilter,
    bayiFilter,
    alisTarihiFilter,
  });

  // Kullanıcı rolüne göre bayi filtresini ayarla
  useEffect(() => {
    if (userRole && userRole !== "admin" && userRole !== "teknik_eleman") {
      setBayiFilter("");
    }
  }, [userRole]);

  // Role göre kolonları filtrele
  const columns = useMemo(() => {
    return getColumnsStokCihaz(userRole);
  }, [userRole]);
  const [columnOrder, setColumnOrder] = useColumnOrder(columns);

  const loadStockItems = useCallback(() => {
    setLoading(true);
    setError(null);
    
    // Bayi filtresi için query parametresi oluştur
    const params = new URLSearchParams();
    if (filtersRef.current.bayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
      // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
      if (filtersRef.current.bayiFilter === "ixpilot") {
        params.append("bayi_id", "null");
      } else {
        params.append("bayi_id", filtersRef.current.bayiFilter);
      }
    }
    
    const url = `/api/stok-cihazlari/${params.toString() ? `?${params.toString()}` : ""}`;
    api
      .get(url)
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error("Stok verileri alınamadı.");
      }
        setStockItems(data);
      })
      .catch((err) => {
        // Merkezi error handling - Güvenli mesaj göster
        const errorMessage = handleError(err, "Stok verileri yükleme hatası");
        setError(errorMessage);
      })
      .finally(() => setLoading(false));
  }, [userRole, handleError, filtersRef]);

  const fetchStockDates = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if ((userRole === "admin" || userRole === "teknik_eleman") && bayiFilter) {
        if (bayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", bayiFilter);
        }
      }
      const url = `/api/stok-cihazlari/dates${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await api.get(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Stok tarihleri alınamadı.");
      setAvailableAlisTarihleri(Array.isArray(data) ? data : []);
    } catch (err) {
      setAvailableAlisTarihleri([]);
    }
  }, [userRole, bayiFilter]);

  useEffect(() => {
    fetchStockDates();
  }, [fetchStockDates]);

  // Listele butonuna basınca filtreleri uygula
  const handleSearch = useCallback(() => {
    const currentFilters = filtersRef.current;
    setAppliedImeiFilter(currentFilters.imeiFilter);
    setAppliedModelFilter(currentFilters.modelFilter);
    setAppliedStatusFilter(currentFilters.statusFilter);
    setAppliedAlisTarihiFilter(currentFilters.alisTarihiFilter);
    setHasSearched(true);
    loadStockItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStockItems]);

  const fetchAksesuarlar = useCallback(async () => {
    setAksesuarlarLoading(true);
    setAksesuarlarError(null);
    try {
      const params = new URLSearchParams();
      if (aksesuarSearch) params.append("q", aksesuarSearch);

      const query = params.toString();
      const res = await api.get(`/api/aksesuarlar/${query ? `?${query}` : ""}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Aksesuar verileri alınamadı.");
      }
      setAksesuarlar(Array.isArray(data) ? data : []);
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = handleError(err, "Aksesuar verileri yükleme hatası");
      setAksesuarlarError(errorMessage);
    } finally {
      setAksesuarlarLoading(false);
    }
  }, [aksesuarSearch, handleError]);

  useEffect(() => {
    fetchAksesuarlar();
  }, [fetchAksesuarlar]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("tr-TR");
  };

  const handleDeleteStock = async (item) => {
    if (!item?.id) return;
    const confirm = window.confirm("Bu stok kaydını silmek istediğinize emin misiniz?");
    if (!confirm) return;
    try {
      setProcessingId(item.id);
      const res = await api.delete(`/api/stok-cihazlari/${item.id}`);
      if (!res.ok) {
        let data;
        try {
          data = await res.json();
        } catch (e) {
          data = null;
        }
        throw new Error(data?.detail || "Stok kaydı silinemedi.");
      }
      toast({
        title: "Stok kaydı silindi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadStockItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Stok kaydı silme hatası");
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkAsReady = async (item) => {
    if (!item?.id) return;
    const confirm = window.confirm("Bu cihazın durumunu 'Hazır' yapmak istediğinize emin misiniz?");
    if (!confirm) return;
    try {
      setProcessingId(item.id);
      const res = await api.post(`/api/stok-cihazlari/${item.id}/mark`, {
        durum: "hazir",
      });
      if (!res.ok) {
        let data;
        try {
          data = await res.json();
        } catch (e) {
          data = null;
        }
        throw new Error(data?.detail || "Durum güncellenemedi.");
      }
      toast({
        title: "Cihaz durumu 'Hazır' olarak güncellendi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadStockItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Stok kaydı silme hatası");
    } finally {
      setProcessingId(null);
    }
  };

  const renderStatusTag = (item) => {
    const color = STATUS_COLOR[item.durum] || "gray";
    const label = STATUS_LABELS[item.durum] || item.durum;
    return (
      <Tag size="sm" colorScheme={color} borderRadius="full" px={3}>
        {label}
      </Tag>
    );
  };

  const renderNote = (item) => {
    const full = item.notlar || "";
    if (!full.trim()) return "-";
    const maxLen = 80;
    const display = full.length > maxLen ? `${full.slice(0, maxLen)}...` : full;
    return (
      <Tooltip label={full} hasArrow placement="top" bg="gray.700" color="white" maxW="320px" whiteSpace="pre-wrap">
        <Text fontSize="12px" color="gray.700" maxW="260px" noOfLines={2} cursor="help">
          {display}
        </Text>
      </Tooltip>
    );
  };

  const openNoteModal = (item) => {
    setNoteModal({
      isOpen: true,
      id: item.id,
      notlar: item.notlar || "",
    });
  };

  const closeNoteModal = () => {
    setNoteModal({ isOpen: false, id: null, notlar: "" });
  };

  const openEditStockModal = (item) => {
    setEditStockModal({
      isOpen: true,
      id: item.id,
      tedarikci: item.tedarikci_raw || "",
      alis_tarihi: item.alis_tarihi_raw || "",
    });
  };

  const closeEditStockModal = () => {
    setEditStockModal({
      isOpen: false,
      id: null,
      tedarikci: "",
      alis_tarihi: "",
    });
  };

  const handleSaveNote = async () => {
    if (!noteModal.id) return;
    try {
      setProcessingId(noteModal.id);
      const res = await api.patch(`/api/stok-cihazlari/${noteModal.id}`, {
        notlar: noteModal.notlar || null,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Not güncellenemedi.");
      toast({
        title: "Not güncellendi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      closeNoteModal();
      loadStockItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Stok kaydı silme hatası");
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveStockEdit = async () => {
    if (!editStockModal.id) return;
    try {
      setProcessingId(editStockModal.id);
      const res = await api.patch(`/api/stok-cihazlari/${editStockModal.id}`, {
        tedarikci: formatTedarikciName(editStockModal.tedarikci) || null,
        alis_tarihi: editStockModal.alis_tarihi || null,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Stok alanları güncellenemedi.");
      toast({
        title: "Stok bilgileri güncellendi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      closeEditStockModal();
      loadStockItems();
    } catch (err) {
      handleError(err, "Stok düzenleme hatası");
    } finally {
      setProcessingId(null);
    }
  };

  const handleStockContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu({
      position: { x: event.clientX, y: event.clientY },
      item,
    });
  };

  const closeContextMenu = () => setContextMenu({ position: null, item: null });

  const tableData = useMemo(() => {
    let filtered = stockItems;
    
    // IMEI filtresi
    if (appliedImeiFilter.trim()) {
      const imeiLower = appliedImeiFilter.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const imei = (item.imei || "").toLowerCase();
        return imei.includes(imeiLower);
      });
    }
    
    // Model filtresi (exact match - dropdown'dan seçildiği için)
    if (appliedModelFilter) {
      filtered = filtered.filter((item) => {
        return item.model === appliedModelFilter;
      });
    }
    
    // Durum filtresi
    if (appliedStatusFilter) {
      filtered = filtered.filter((item) => item.durum === appliedStatusFilter);
    }

    // Alış tarihi filtresi (tek tarih)
    if (appliedAlisTarihiFilter) {
      filtered = filtered.filter((item) => {
        if (!item.alis_tarihi) return false;
        const itemDate = new Date(item.alis_tarihi);
        if (Number.isNaN(itemDate.getTime())) return false;
        return itemDate.toISOString().slice(0, 10) === appliedAlisTarihiFilter;
      });
    }
    
    return filtered.map((item) => {
      // Protokol değerini normalize et - boş string, null, undefined kontrolü
      let protokolValue = item.protokol;
      if (!protokolValue || (typeof protokolValue === 'string' && protokolValue.trim() === '')) {
        protokolValue = "-";
      } else {
        // Protokol değerini küçük harfe çevirip ilk harfi büyük yap (örn: "teltonika" -> "Teltonika")
        protokolValue = protokolValue.trim().toLowerCase();
        protokolValue = protokolValue.charAt(0).toUpperCase() + protokolValue.slice(1);
      }
      
      return {
      id: item.id,
      durum: renderStatusTag(item),
        durum_raw: item.durum, // Context menu için raw durum bilgisi
      imei: item.imei,
      model: item.model || "-",
        protokol: protokolValue,
        is_ortagi: item.is_ortagi || "ixPilot",
      alis_tarihi: formatDate(item.alis_tarihi),
      alis_tarihi_raw: item.alis_tarihi || "",
      tedarikci: item.tedarikci || "-",
      tedarikci_raw: item.tedarikci || "",
      kira_satis_tarihi: formatDate(item.kira_satis_tarihi),
      kira_satis_tarihi_raw: item.kira_satis_tarihi || "",
      plaka: item.plaka ? <Badge colorScheme="blue">{item.plaka}</Badge> : "-",
      firma: item.firma || "-",
      notlar: renderNote(item),
      };
    });
  }, [
    stockItems,
    appliedImeiFilter,
    appliedModelFilter,
    appliedStatusFilter,
    appliedAlisTarihiFilter,
  ]);

  const handleSaveStock = async () => {
    if (!newStock.imei.trim()) {
      toast({ title: "IMEI gerekli", status: "warning" });
      return;
    }
    if (!newStock.model || !newStock.model.trim()) {
      toast({ title: "Cihaz modeli zorunludur", status: "warning" });
      return;
    }
    try {
      setSavingStock(true);
      // Model'e göre protokolü otomatik belirle
      const protokol = getProtocolFromModel(newStock.model);
      
      const payload = {
        imei: newStock.imei.trim(),
        model: newStock.model.trim(),
        durum: "hazir", // Her zaman hazir olarak eklenir
        alis_tarihi: newStock.alis_tarihi || null,
        tedarikci: formatTedarikciName(newStock.tedarikci) || null,
        notlar: newStock.notlar || null,
        protokol: protokol,
        bataryali: newStock.bataryali || false,
        bayi_id: newStock.bayi_id ? parseInt(newStock.bayi_id) : null,
      };
      const res = await api.post("/api/stok-cihazlari/", payload);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Stok kaydı oluşturulamadı.");
      toast({ 
        title: "Stok kaydı eklendi", 
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setNewStock({
        imei: "",
        model: "",
        alis_tarihi: "",
        tedarikci: "",
        notlar: "",
        bataryali: false,
        bayi_id: "",
      });
      addModal.onClose();
      loadStockItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Stok kaydı ekleme hatası");
    } finally {
      setSavingStock(false);
    }
  };

  const parseBulkImeis = (rawValue) => {
    if (!rawValue || !rawValue.trim()) {
      return [];
    }
    // Virgül, noktalı virgül ve yeni satır ile ayır, trim yap ve boş olanları filtrele
    return rawValue
      .split(/[\n,;]+/)
      .map((item) => item.trim())
      .filter((item) => item && item.length > 0); // Boş string'leri ve sadece boşluk olanları filtrele
  };

  const handleSaveBulkStock = async () => {
    const imeis = parseBulkImeis(bulkStock.imeiList);
    if (!imeis.length) {
      toast({ title: "En az bir IMEI girin", status: "warning" });
      return;
    }
    if (!bulkStock.model || !bulkStock.model.trim()) {
      toast({ title: "Cihaz modeli zorunludur", status: "warning" });
      return;
    }
    try {
      setSavingBulkStock(true);
      // Model'e göre protokolü otomatik belirle
      const protokol = getProtocolFromModel(bulkStock.model);
      
      const payload = {
        imeis,
        model: bulkStock.model.trim(),
        durum: "hazir", // Her zaman hazir olarak eklenir
        alis_tarihi: bulkStock.alis_tarihi || null,
        tedarikci: formatTedarikciName(bulkStock.tedarikci) || null,
        notlar: bulkStock.notlar || null,
        protokol: protokol,
        bataryali: bulkStock.bataryali || false,
        bayi_id: bulkStock.bayi_id ? parseInt(bulkStock.bayi_id) : null,
      };
      const res = await api.post("/api/stok-cihazlari/bulk", payload);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Toplu stok kaydı eklenemedi.");
      toast({
        title: "Toplu stok eklendi",
        description: `${Array.isArray(data) ? data.length : imeis.length} cihaz eklendi`,
        status: "success",
      });
      setBulkStock({
        imeiList: "",
        model: "",
        alis_tarihi: "",
        tedarikci: "",
        notlar: "",
        bataryali: false,
        bayi_id: "",
      });
      bulkModal.onClose();
      loadStockItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Toplu stok ekleme hatası");
    } finally {
      setSavingBulkStock(false);
    }
  };

  const handleSaveAksesuar = async () => {
    if (!newAksesuar.name.trim()) {
      toast({ title: "Aksesuar adı gerekli", status: "warning" });
      return;
    }
    const parsedQuantity = Number(newAksesuar.quantity);
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 0) {
      toast({ title: "Miktar 0 veya daha büyük tam sayı olmalı", status: "warning" });
      return;
    }
    try {
      setSavingAksesuar(true);
      const payload = {
        name: newAksesuar.name.trim(),
        quantity: parsedQuantity,
      };
      const url = editingAksesuar
        ? `/api/aksesuarlar/${editingAksesuar.id}`
        : "/api/aksesuarlar/";
      const res = editingAksesuar
        ? await api.patch(url, payload)
        : await api.post(url, payload);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Aksesuar kaydı oluşturulamadı.");
      toast({
        title: editingAksesuar ? "Aksesuar güncellendi" : "Aksesuar eklendi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setNewAksesuar({ name: "", quantity: "0" });
      setEditingAksesuar(null);
      addAksesuarModal.onClose();
      fetchAksesuarlar();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Aksesuar kaydı hatası");
    } finally {
      setSavingAksesuar(false);
    }
  };

  const handleEditAksesuar = useCallback((aksesuar) => {
    setEditingAksesuar(aksesuar);
    setNewAksesuar({
      name: aksesuar.name,
      quantity: String(aksesuar.quantity ?? 0),
    });
    addAksesuarModal.onOpen();
  }, [addAksesuarModal]);

  const handleDeleteAksesuar = useCallback(async (aksesuarId) => {
    const confirm = window.confirm("Bu aksesuarı silmek istediğinize emin misiniz?");
    if (!confirm) return;
    try {
      const res = await api.delete(`/api/aksesuarlar/${aksesuarId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.detail || "Aksesuar silinemedi.");
      }
      toast({ 
        title: "Aksesuar silindi", 
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchAksesuarlar();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Aksesuar silme hatası");
    }
  }, [toast, fetchAksesuarlar, handleError]);

  const aksesuarTableData = useMemo(() => {
    return aksesuarlar.map((item) => ({
      id: item.id,
      name: item.name,
      quantity: item.quantity ?? 0,
      actions: (
        <HStack spacing={2} justify="center" w="100%">
          <Button
            size="xs"
            colorScheme="blue"
            variant="outline"
            onClick={() => handleEditAksesuar(item)}
            fontSize="12px"
          >
            Düzenle
          </Button>
          <Button
            size="xs"
            colorScheme="red"
            variant="outline"
            onClick={() => handleDeleteAksesuar(item.id)}
            fontSize="12px"
          >
            Sil
          </Button>
        </HStack>
      ),
    }));
  }, [aksesuarlar, handleEditAksesuar, handleDeleteAksesuar]);

  return (
    <Box position="relative" mt={{ base: 4, md: 6 }}>
      <Box
        bg="transparent"
        borderRadius="0"
        p={0}
        border="none"
        boxShadow="none"
        mb={4}
      >
        {stokTuru === "cihaz" ? (
          <>
              <FilterPanel
                fieldWidth={{ base: "100%", md: "130px", xl: "140px" }}
                buttonSize="xs"
                gap={2}
                fields={[
                  {
                    key: "stok_turu",
                    type: "select",
                    placeholder: "Stok Türü",
                    value: stokTuru,
                    onChange: setStokTuru,
                    options: [
                      { label: "Cihazlar", value: "cihaz" },
                      { label: "Aksesuarlar", value: "aksesuar" },
                    ],
                  },
                  ...(userRole === "admin" || userRole === "teknik_eleman" ? [{
                    key: "bayi",
                    type: "select",
                    label: "Bayi",
                    placeholder: "Bayi Seçiniz",
                    value: bayiFilter,
                    onChange: setBayiFilter,
                    options: [
                      { label: "Tümü", value: "" },
                      { label: "ixPilot", value: "ixpilot" },
                      ...bayiler.map((bayi) => ({
                        label: bayi.name,
                        value: bayi.id,
                      })),
                    ],
                    isDisabled: bayilerLoading,
                  }] : []),
                  {
                    key: "imei",
                    type: "text",
                    placeholder: "IMEI",
                    value: imeiFilter,
                    onChange: setImeiFilter,
                  },
                  {
                    key: "model",
                    type: "select",
                    placeholder: "Model",
                    value: modelFilter,
                    onChange: setModelFilter,
                    options: [
                      { label: "Tümü", value: "" },
                      ...DEVICE_MODELS.map((model) => ({
                        label: model,
                        value: model,
                      })),
                    ],
                  },
                  {
                    key: "status",
                    type: "select",
                    placeholder: "Durum",
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: STATUS_OPTIONS,
                  },
                  {
                    key: "alis_tarihi",
                    type: "select",
                    placeholder: "Satın Alma Tarihi",
                    value: alisTarihiFilter,
                    onChange: setAlisTarihiFilter,
                    options: [
                      { label: "Tümü", value: "" },
                      ...availableAlisTarihleri.map((dateValue) => ({
                        label: formatDate(dateValue),
                        value: dateValue,
                      })),
                    ],
                  },
                ]}
                onSearch={handleSearch}
                searchButtonLoading={loading}
                searchButtonLoadingText="Yükleniyor..."
                onReset={() => {
                  setImeiFilter("");
                  setModelFilter("");
                  setStatusFilter("");
                  setAlisTarihiFilter("");
                  // Reset'te admin/teknik_eleman için "ixpilot" kalır
                  setBayiFilter((userRole === "admin" || userRole === "teknik_eleman") ? "ixpilot" : "");
                  setAppliedImeiFilter("");
                  setAppliedModelFilter("");
                  setAppliedStatusFilter("");
                  setAppliedAlisTarihiFilter("");
                  setHasSearched(false);
                }}
                actions={[
                  {
                    key: "add",
                    label: "Stok Ekle",
                    onClick: addModal.onOpen,
                    buttonProps: { colorScheme: "blue", leftIcon: <FiPlus /> },
                  },
                  {
                    key: "bulk",
                    label: "Toplu Ekle",
                    onClick: () => {
                      setBulkStock({
                        imeiList: "",
                        model: "",
                        alis_tarihi: "",
                        tedarikci: "",
                        notlar: "",
                        bayi_id: "",
                      });
                      bulkModal.onOpen();
                    },
                    buttonProps: { colorScheme: "blue", leftIcon: <FiUpload /> },
                  },
                ]}
              />

            {error && (
              <Box bg="red.50" border="1px solid" borderColor="red.100" p={3} borderRadius="md" mb={3}>
                  <Text fontSize="sm" color="red.600">
                  {error}
                </Text>
                </Box>
            )}

            <DataTable
              title={`Stok Cihazları • ${tableData.length} cihaz`}
              columns={columns}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              data={tableData}
              topPadding={{ base: "56px", md: "24px" }}
              loading={loading}
              hasSearched={hasSearched}
              onRowContextMenu={handleStockContextMenu}
              emptyMessage="Arama kriterlerinize uygun kayıt bulunamadı."
            />
          </>
        ) : (
          <>
              <FilterPanel
                fieldWidth={{ base: "100%", md: "150px", xl: "180px" }}
                buttonSize="xs"
                gap={2}
                fields={[
                  {
                    key: "stok_turu",
                    type: "select",
                    placeholder: "Stok Türü",
                    value: stokTuru,
                    onChange: setStokTuru,
                    options: [
                      { label: "Cihazlar", value: "cihaz" },
                      { label: "Aksesuarlar", value: "aksesuar" },
                    ],
                  },
                  {
                    key: "search",
                    type: "text",
                    placeholder: "Aksesuar",
                    value: aksesuarSearch,
                    onChange: setAksesuarSearch,
                  },
                ]}
                onReset={() => setAksesuarSearch("")}
                actions={[
                  {
                    key: "add",
                    label: "Aksesuar Ekle",
                    onClick: () => {
                      setEditingAksesuar(null);
                      setNewAksesuar({ name: "", quantity: "0" });
                      addAksesuarModal.onOpen();
                    },
                    buttonProps: { colorScheme: "blue", leftIcon: <FiPlus /> },
                  },
                ]}
              />

            {aksesuarlarError && (
              <Box bg="red.50" border="1px solid" borderColor="red.100" p={3} borderRadius="md" mb={3}>
                  <Text fontSize="sm" color="red.600">
                  {aksesuarlarError}
                </Text>
              </Box>
            )}

            <DataTable
              title={`Aksesuarlar • ${aksesuarTableData.length} aksesuar`}
              columns={columnsStokAksesuar}
              columnOrder={["name", "quantity", "actions"]}
              data={aksesuarTableData}
              minTableWidth="100%"
              topPadding={{ base: "56px", md: "24px" }}
              loading={aksesuarlarLoading}
              emptyMessage="Henüz aksesuar kaydı yok. 'Aksesuar Ekle' butonuna tıklayarak yeni kayıt ekleyebilirsiniz."
            />
          </>
        )}
      </Box>

      {/* Context Menu */}
      <StokContextMenu
        position={contextMenu.position}
        data={contextMenu.item}
        onClose={closeContextMenu}
        onOpenEditModal={openEditStockModal}
        onOpenNoteModal={openNoteModal}
        onDeleteStock={handleDeleteStock}
        onMarkAsReady={handleMarkAsReady}
      />

      {/* Stok Ekle Modal */}
      <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" fontWeight="semibold">Yeni Stok Kaydı</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="13px" fontWeight="medium">IMEI</FormLabel>
              <Input
                value={newStock.imei}
                maxLength={25}
                placeholder="IMEI (en fazla 25 karakter)"
                onChange={(e) =>
                  setNewStock((prev) => ({
                    ...prev,
                    imei: e.target.value.replace(/\s+/g, "").slice(0, 25),
                  }))
                }
              />
              <FormHelperText fontSize="11px" color="gray.500">
                Maksimum 25 karakter girilebilir.
              </FormHelperText>
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="13px" fontWeight="medium">Cihaz Modeli</FormLabel>
              <Select
                placeholder="Cihaz Seçiniz"
                value={newStock.model}
                onChange={(e) => setNewStock((prev) => ({ ...prev, model: e.target.value }))}
              >
                {DEVICE_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="13px" fontWeight="medium">Alış Tarihi</FormLabel>
              <Input
                type="date"
                value={newStock.alis_tarihi}
                onChange={(e) => setNewStock((prev) => ({ ...prev, alis_tarihi: e.target.value }))}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="13px" fontWeight="medium">Tedarikçi</FormLabel>
              <Input
                value={newStock.tedarikci}
                onChange={(e) => setNewStock((prev) => ({ ...prev, tedarikci: e.target.value }))}
                placeholder="Tedarikçi adı"
              />
            </FormControl>
            <FormControl mb={3}>
              <Checkbox
                isChecked={newStock.bataryali}
                onChange={(e) => setNewStock((prev) => ({ ...prev, bataryali: e.target.checked }))}
              >
                <Text fontSize="13px" fontWeight="medium">Bataryalı</Text>
              </Checkbox>
              <FormHelperText fontSize="11px" color="gray.500">
                Cihaz bataryalı ise işaretleyiniz.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="13px" fontWeight="medium">Not</FormLabel>
              <Textarea
                rows={3}
                value={newStock.notlar}
                onChange={(e) => setNewStock((prev) => ({ ...prev, notlar: e.target.value }))}
                placeholder="Not ekleyin..."
              />
            </FormControl>
            {(userRole === "admin" || userRole === "teknik_eleman") && (
              <FormControl mb={3}>
                <FormLabel fontSize="13px" fontWeight="medium">Bayi (Opsiyonel)</FormLabel>
                <Select
                  placeholder="Bayi seçiniz (boş bırakılırsa admin'e eklenir)"
                  value={newStock.bayi_id}
                  onChange={(e) => setNewStock((prev) => ({ ...prev, bayi_id: e.target.value }))}
                >
                  {bayiler.map((bayi) => (
                    <option key={bayi.id} value={bayi.id}>
                      {bayi.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="11px" color="gray.500">
                  Stok kaydını bir bayiye atamak için seçiniz. Boş bırakılırsa admin'e eklenir.
                </FormHelperText>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={addModal.onClose}>
              İptal
            </Button>
            <Button colorScheme="blue" onClick={handleSaveStock} isLoading={savingStock}>
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Toplu Stok Ekle Modal */}
      <Modal isOpen={bulkModal.isOpen} onClose={bulkModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" fontWeight="semibold">Toplu Stok Ekle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="13px" fontWeight="medium">IMEI Listesi</FormLabel>
              <Textarea
                rows={6}
                placeholder="Her satıra bir IMEI girin veya virgülle ayırın"
                value={bulkStock.imeiList}
                onChange={(e) => setBulkStock((prev) => ({ ...prev, imeiList: e.target.value }))}
              />
              <FormHelperText fontSize="11px" color="gray.500" mt={1}>
                Örnek: 123456789012345, 987654321098765
              </FormHelperText>
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="13px" fontWeight="medium">Cihaz Modeli</FormLabel>
              <Select
                placeholder="Cihaz Seçiniz"
                value={bulkStock.model}
                onChange={(e) => setBulkStock((prev) => ({ ...prev, model: e.target.value }))}
              >
                {DEVICE_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="13px" fontWeight="medium">Alış Tarihi</FormLabel>
              <Input
                type="date"
                value={bulkStock.alis_tarihi}
                onChange={(e) => setBulkStock((prev) => ({ ...prev, alis_tarihi: e.target.value }))}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="13px" fontWeight="medium">Tedarikçi</FormLabel>
              <Input
                value={bulkStock.tedarikci}
                onChange={(e) => setBulkStock((prev) => ({ ...prev, tedarikci: e.target.value }))}
                placeholder="Tedarikçi adı"
              />
            </FormControl>
            <FormControl mb={3}>
              <Checkbox
                isChecked={bulkStock.bataryali}
                onChange={(e) => setBulkStock((prev) => ({ ...prev, bataryali: e.target.checked }))}
              >
                <Text fontSize="13px" fontWeight="medium">Bataryalı</Text>
              </Checkbox>
              <FormHelperText fontSize="11px" color="gray.500">
                Cihazlar bataryalı ise işaretleyiniz.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="13px" fontWeight="medium">Not</FormLabel>
              <Textarea
                rows={3}
                value={bulkStock.notlar}
                onChange={(e) => setBulkStock((prev) => ({ ...prev, notlar: e.target.value }))}
                placeholder="Not ekleyin..."
              />
            </FormControl>
            {(userRole === "admin" || userRole === "teknik_eleman") && (
              <FormControl mb={3}>
                <FormLabel fontSize="13px" fontWeight="medium">Bayi (Opsiyonel)</FormLabel>
                <Select
                  placeholder="Bayi seçiniz (boş bırakılırsa admin'e eklenir)"
                  value={bulkStock.bayi_id}
                  onChange={(e) => setBulkStock((prev) => ({ ...prev, bayi_id: e.target.value }))}
                >
                  {bayiler.map((bayi) => (
                    <option key={bayi.id} value={bayi.id}>
                      {bayi.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="11px" color="gray.500">
                  Stok kayıtlarını bir bayiye atamak için seçiniz. Boş bırakılırsa admin'e eklenir.
                </FormHelperText>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={bulkModal.onClose}>
              İptal
            </Button>
            <Button colorScheme="blue" onClick={handleSaveBulkStock} isLoading={savingBulkStock}>
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Not Düzenle Modal */}
      <Modal isOpen={noteModal.isOpen} onClose={closeNoteModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" fontWeight="semibold">Notu Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="13px" fontWeight="medium">Not</FormLabel>
              <Textarea
                rows={4}
                value={noteModal.notlar}
                onChange={(e) =>
                  setNoteModal((prev) => ({ ...prev, notlar: e.target.value }))
                }
                placeholder="Not ekleyin..."
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeNoteModal}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveNote}
              isLoading={processingId === noteModal.id}
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Alış Tarihi / Tedarikçi Düzenle Modal */}
      <Modal isOpen={editStockModal.isOpen} onClose={closeEditStockModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" fontWeight="semibold">Stok Bilgilerini Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel fontSize="13px" fontWeight="medium">Alış Tarihi</FormLabel>
              <Input
                type="date"
                value={editStockModal.alis_tarihi}
                onChange={(e) =>
                  setEditStockModal((prev) => ({ ...prev, alis_tarihi: e.target.value }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="13px" fontWeight="medium">Tedarikçi</FormLabel>
              <Input
                value={editStockModal.tedarikci}
                placeholder="Tedarikçi adı"
                onChange={(e) =>
                  setEditStockModal((prev) => ({ ...prev, tedarikci: e.target.value }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeEditStockModal}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveStockEdit}
              isLoading={processingId === editStockModal.id}
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Aksesuar Ekle/Düzenle Modal */}
      <Modal isOpen={addAksesuarModal.isOpen} onClose={addAksesuarModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" fontWeight="semibold">
            {editingAksesuar ? "Aksesuar Düzenle" : "Yeni Aksesuar"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="13px" fontWeight="medium">Aksesuar Adı</FormLabel>
              <Input
                value={newAksesuar.name}
                onChange={(e) => setNewAksesuar((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Örn: GPS Anteni, Kablolar, vb."
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="13px" fontWeight="medium">Miktar</FormLabel>
              <Input
                type="number"
                min={0}
                step={1}
                value={newAksesuar.quantity}
                onChange={(e) => setNewAksesuar((prev) => ({ ...prev, quantity: e.target.value }))}
                placeholder="Örn: 10"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={addAksesuarModal.onClose}>
              İptal
            </Button>
            <Button colorScheme="blue" onClick={handleSaveAksesuar} isLoading={savingAksesuar}>
              {editingAksesuar ? "Güncelle" : "Kaydet"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
